import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  Policy as PolicyIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/api';

// A policy changed within this window is flagged as recently updated
const RECENTLY_UPDATED_DAYS = 30;

const isRecentlyUpdated = (updatedAt) => {
  if (!updatedAt) return false;
  const ageInDays = (Date.now() - new Date(updatedAt).getTime()) / (1000 * 60 * 60 * 24);
  return ageInDays <= RECENTLY_UPDATED_DAYS;
};

const UpdatedPoliciesTab = () => {
  const { user, tokenManager } = useAuth();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);

  const [currentPolicy, setCurrentPolicy] = useState(null);
  const [viewUrl, setViewUrl] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const authHeaders = useCallback(
    () => ({ Authorization: `Bearer ${tokenManager.getToken()}` }),
    [tokenManager]
  );

  useEffect(() => {
    const fetchPolicies = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/policies`, { headers: authHeaders() });

        if (response.ok) {
          const result = await response.json();
          setPolicies(result.policies || []);
        } else {
          toast.error('Failed to load policies');
        }
      } catch (error) {
        console.error('Error fetching policies:', error);
        toast.error('Failed to load policies');
      } finally {
        setLoading(false);
      }
    };

    if (user) fetchPolicies();
  }, [user, authHeaders]);

  // Release the blob URL when the viewer closes or switches document
  useEffect(() => {
    return () => {
      if (viewUrl) window.URL.revokeObjectURL(viewUrl);
    };
  }, [viewUrl]);

  const handleView = async (policy) => {
    setCurrentPolicy(policy);
    setViewLoading(true);

    try {
      const response = await fetch(`${API_URL}/policies/${policy.policyId}/view`, {
        headers: authHeaders(),
      });

      if (!response.ok) {
        toast.error('Failed to open policy');
        setCurrentPolicy(null);
        return;
      }

      const blob = await response.blob();
      setViewUrl(window.URL.createObjectURL(blob));
    } catch (error) {
      console.error('Error opening policy:', error);
      toast.error('Failed to open policy');
      setCurrentPolicy(null);
    } finally {
      setViewLoading(false);
    }
  };

  const handleCloseView = () => {
    if (viewUrl) window.URL.revokeObjectURL(viewUrl);
    setViewUrl(null);
    setCurrentPolicy(null);
  };

  const handleDownload = async (policy) => {
    try {
      toast.info(`Downloading ${policy.title}...`);

      const response = await fetch(`${API_URL}/policies/${policy.policyId}/download`, {
        headers: authHeaders(),
      });

      if (!response.ok) {
        if (response.status === 401) {
          toast.error('Authentication failed. Please login again.');
        } else {
          toast.error('Download failed');
        }
        return;
      }

      const blob = await response.blob();

      // The backend sets a properly formatted filename on the header
      let downloadName = policy.title;
      const contentDisposition = response.headers.get('content-disposition');
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="([^"]+)"/);
        if (match && match[1]) downloadName = match[1];
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadName;
      link.style.display = 'none';
      document.body.appendChild(link);

      requestAnimationFrame(() => {
        link.click();
        setTimeout(() => {
          if (document.body.contains(link)) document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 500);
      });
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download policy');
    }
  };

  const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : null);

  // Group policies by category so each category renders as its own section
  const groupedByCategory = policies.reduce((acc, policy) => {
    if (!acc[policy.category]) acc[policy.category] = [];
    acc[policy.category].push(policy);
    return acc;
  }, {});

  if (!user) return <div>Loading...</div>;

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Updated Policies
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Read and download the latest company policies and guidelines. Policies marked
          &ldquo;Updated&rdquo; have changed in the last {RECENTLY_UPDATED_DAYS} days.
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : policies.length === 0 ? (
        <Alert severity="info">No policies have been published yet.</Alert>
      ) : (
        Object.entries(groupedByCategory).map(([category, categoryPolicies]) => (
          <Box key={category} sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{
                mb: 3,
                fontSize: '1.1rem',
                fontWeight: 600,
                color: '#2c3e50',
                borderBottom: '2px solid #e8eaed',
                pb: 1,
              }}
            >
              {category}
            </Typography>
            <Grid container spacing={3}>
              {categoryPolicies.map((policy) => (
                <Grid item xs={12} md={6} key={policy.policyId}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                        <PolicyIcon color="primary" fontSize="small" sx={{ mt: 0.3 }} />
                        <Typography variant="h6" component="div" sx={{ fontSize: '1rem', flexGrow: 1 }}>
                          {policy.title}
                        </Typography>
                        {isRecentlyUpdated(policy.updatedAt) && (
                          <Chip label="Updated" color="warning" size="small" />
                        )}
                      </Box>

                      {policy.description && (
                        <Typography variant="body2" color="text.secondary" paragraph>
                          {policy.description}
                        </Typography>
                      )}

                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        <Chip label={`v${policy.version}`} size="small" variant="outlined" />
                        {policy.effectiveDate && (
                          <Chip
                            label={`Effective ${formatDate(policy.effectiveDate)}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {policy.fileSizeFormatted && (
                          <Chip label={policy.fileSizeFormatted} size="small" variant="outlined" />
                        )}
                      </Box>

                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                        Last updated: {formatDate(policy.updatedAt)}
                      </Typography>
                    </CardContent>
                    <CardActions sx={{ px: 2, pb: 2, gap: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleView(policy)}
                        sx={{ fontSize: '0.85rem' }}
                      >
                        Read
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={() => handleDownload(policy)}
                        sx={{ fontSize: '0.85rem' }}
                      >
                        Download
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))
      )}

      {/* Policy reader */}
      <Dialog open={!!currentPolicy} onClose={handleCloseView} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 600, pr: 6 }}>
          {currentPolicy?.title}
          <IconButton
            aria-label="close"
            onClick={handleCloseView}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {viewLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
              <CircularProgress />
            </Box>
          ) : currentPolicy && viewUrl ? (
            currentPolicy.isImage ? (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <img
                  src={viewUrl}
                  alt={currentPolicy.title}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '600px',
                    objectFit: 'contain',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                  }}
                />
              </Box>
            ) : currentPolicy.isPDF ? (
              <iframe
                src={viewUrl}
                width="100%"
                height="600px"
                title={currentPolicy.title}
                style={{ border: 'none' }}
              />
            ) : (
              <Alert severity="info">
                This policy is a Word or Excel document and cannot be shown in the browser.
                Please download it to read.
              </Alert>
            )
          ) : null}
        </DialogContent>
        <DialogActions>
          {currentPolicy && (
            <Button startIcon={<DownloadIcon />} onClick={() => handleDownload(currentPolicy)}>
              Download
            </Button>
          )}
          <Button onClick={handleCloseView}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UpdatedPoliciesTab;
